import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLegalLine } from "@/components/AuthLegalLine";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "planner" });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please agree to all three NContro documents to continue");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, accepted_terms: true, accepted_privacy: true });
      toast.success("Welcome to NContro");
      nav(u.role === "vendor" ? "/vendor/dashboard" : "/planner/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  const termsPath = form.role === "vendor" ? "/terms/vendor" : "/terms/planner";
  const termsLabel = form.role === "vendor" ? "Vendor Agreement" : "Customer Terms of Service";

  return (
    <div data-testid="register-page" className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-8 lg:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-3">Create account</div>
          <h1 className="font-serif text-4xl sm:text-5xl mb-8 sm:mb-10">Join NContro</h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="eyebrow">I am a</Label>
              <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="grid grid-cols-2 gap-3 mt-2">
                <label data-testid="role-planner" htmlFor="r-planner" className={`card-elegant p-4 cursor-pointer ${form.role === "planner" ? "border-[color:var(--accent)] ring-1 ring-[color:var(--accent)]" : ""}`}>
                  <RadioGroupItem value="planner" id="r-planner" className="sr-only" />
                  <div className="font-serif text-xl">Event Planner</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">Discover and book vendors</div>
                </label>
                <label data-testid="role-vendor" htmlFor="r-vendor" className={`card-elegant p-4 cursor-pointer ${form.role === "vendor" ? "border-[color:var(--accent)] ring-1 ring-[color:var(--accent)]" : ""}`}>
                  <RadioGroupItem value="vendor" id="r-vendor" className="sr-only" />
                  <div className="font-serif text-xl">Vendor</div>
                  <div className="text-xs text-[#5C5C5C] mt-1">List services & receive quotes</div>
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label className="eyebrow">Full name</Label>
              <Input data-testid="register-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-2 h-12 bg-white" />
            </div>
            <div>
              <Label className="eyebrow">Email</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-2 h-12 bg-white" />
            </div>
            <div>
              <Label className="eyebrow">Password</Label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="mt-2 h-12 bg-white" />
            </div>

            <label className="flex items-start gap-3 pt-2 cursor-pointer">
              <Checkbox data-testid="register-agree" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} className="mt-1" />
              <span className="text-sm leading-relaxed text-[color:var(--text-muted)]">
                I have read and agree to NContro's{" "}
                <Link data-testid="register-terms-link" to={termsPath} target="_blank" className="text-[color:var(--text)] link-underline font-medium">
                  {termsLabel}
                </Link>
                ,{" "}
                <Link data-testid="register-payment-link" to="/payment-policy" target="_blank" className="text-[color:var(--text)] link-underline font-medium">
                  Payment &amp; Refund Policy
                </Link>
                , and{" "}
                <Link data-testid="register-privacy-link" to="/privacy" target="_blank" className="text-[color:var(--text)] link-underline font-medium">
                  Privacy Policy
                </Link>
                {form.role === "vendor" ? (
                  <>, and confirm I am the authorized representative of my business.</>
                ) : (
                  <>, and confirm I am at least 18 years old.</>
                )}
              </span>
            </label>

            <Button data-testid="register-submit" disabled={loading || !agreed} className="btn-sage rounded-full w-full h-12 mt-2">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="text-sm text-[#5C5C5C] mt-8">
            Already have an account? <Link to="/login" className="link-underline text-black">Sign in</Link>
          </div>
          <AuthLegalLine className="mt-6" />
        </div>
      </div>
      <div className="hidden lg:block relative overflow-hidden order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1561593367-66c79c2294e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxldmVudCUyMGZsb3Jpc3QlMjBmbG9yYWwlMjBhcnJhbmdlbWVudCUyMHRhYmxlfGVufDB8fHx8MTc4NTM0NjE2OHww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        <div className="absolute bottom-12 right-12 text-white text-right">
          <div className="eyebrow text-white/80 mb-3">Welcome</div>
          <h2 className="font-serif text-5xl">A collective<br/>of tastemakers.</h2>
        </div>
      </div>
    </div>
  );
}
